import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { IconNote } from '@tabler/icons-react';
import { Updater } from '@tanstack/react-table';
import { AssetClassToLoanType } from 'app-level/config/assetClassTypes';
import {
  BaseTableProps,
  LoanColumnDef,
  LoanTable,
  getDefaultColumns,
} from 'features/drilldown/LoanDatatable/LoanTable';
import { TableQueryParameters } from 'hooks/useTableQueryParams';
import keyBy from 'lodash/keyBy';
import isEqual from 'react-fast-compare';
import { IconButton, useDialog } from 'ui-kit';

import { DealRole } from '__generated__/globalTypes';

import {
  GetDealDiligence_deal,
  GetDealDiligence_deal_diligence,
} from 'query/__generated__/GetDealDiligence';

import { useLoanDiligence } from './contexts/LoanDiligenceProvider';
import DiligenceNotesDialog from './DiligenceNotesDialog';
import { LoanDiligenceStatus, LoanStatusDropdown } from './LoanDiligenceStatus';
import { useDiligenceNotes } from './useDiligenceNotes';
import { DataContext, DataContextActionType } from '../../../../context/DataContext';

export type LoanDiligenceStatusMap = {
  [loanId: string]: LoanDiligenceStatus;
};

interface DiligenceLoanTableProps<K extends keyof AssetClassToLoanType> {
  deal?: GetDealDiligence_deal | null;
  role: DealRole;
  assetClass: K;

  sorting: BaseTableProps<K>['sorting'];

  onViewClick: (loanId: string) => void;
  onQuickStatusClick: (loanId: string) => void;
  onLoanDiligenceStatusChanged: (loanId: string, status: LoanDiligenceStatus) => void;
  pagination: {
    updateParams: (newParams: Partial<TableQueryParameters<any, any>>) => void;

    queryParams: TableQueryParameters<any, any>;
    totalItems?: number;
  };
}

const _ReviewDiligenceTable = <K extends keyof AssetClassToLoanType>({
  deal,
  role,
  assetClass,
  sorting,
  onViewClick,
  onQuickStatusClick,
  onLoanDiligenceStatusChanged,
  pagination,
}: DiligenceLoanTableProps<K>) => {
  /* eslint-disable */
  const loanDiligence = useLoanDiligence();
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const diligenceNoteDialog = useDialog();
  const { state, dispatch } = useContext(DataContext);

  // Create a computed column order that always has 'status' first
  const computedColumnOrder = useMemo(() => {
    const savedOrder = state.globalTableOrderConfig.myPortfolio.loanTable[assetClass] ?? [];
    const orderWithoutStatus = (savedOrder as string[]).filter((colId) => colId !== 'status');
    return ['status', ...orderWithoutStatus];
  }, [state.globalTableOrderConfig.myPortfolio.loanTable[assetClass], assetClass]);

  // Custom setColumnOrder that filters out 'status' column since we always want it first
  const setColumnOrder = useCallback(
    (updater: Updater<string[]>) => {
      // Use computedColumnOrder as the current order for the updater function
      const newOrder = typeof updater === 'function' ? updater(computedColumnOrder) : updater;

      // Filter out 'status' from the new order since we always want it first
      const filteredOrder = newOrder.filter((colId) => colId !== 'status');

      dispatch({
        type: DataContextActionType.SET_GLOBAL_TABLE_ORDER_CONFIG,
        payload: {
          ...state.globalTableOrderConfig,
          myPortfolio: {
            ...state.globalTableOrderConfig.myPortfolio,
            loanTable: {
              ...state.globalTableOrderConfig.myPortfolio.loanTable,
              [assetClass]: filteredOrder as (keyof AssetClassToLoanType[K])[],
            },
          },
        },
      });
    },
    [computedColumnOrder, state.globalTableOrderConfig, assetClass, dispatch],
  );

  // Override the global state temporarily for this component
  const stateWithStatusFirst = useMemo(
    () => ({
      ...state,
      globalTableOrderConfig: {
        ...state.globalTableOrderConfig,
        myPortfolio: {
          ...state.globalTableOrderConfig.myPortfolio,
          loanTable: {
            ...state.globalTableOrderConfig.myPortfolio.loanTable,
            [assetClass]: computedColumnOrder as (keyof AssetClassToLoanType[K])[],
          },
        },
      },
    }),
    [state, computedColumnOrder, assetClass],
  );

  const [localStatusChanges, setLocalStatusChanges] = useState<{
    [loanId: string]: LoanDiligenceStatus;
  }>({});
  const [updateTrigger, setUpdateTrigger] = useState(0);

  const diligenceLoans = useMemo(() => {
    const loans =
      deal?.diligence?.map((diligence) => {
        const loan = diligence.loan as AssetClassToLoanType[K];
        return {
          ...loan,
          status: localStatusChanges[loan.id] || diligence.status,
        };
      }) || [];
    return loans;
  }, [deal?.diligence, localStatusChanges]);

  const loanIdToDiligenceMap = useMemo(() => {
    if (!deal) return {};
    const baseMap = keyBy(deal.diligence, (diligence) => diligence.loan.id);
    return Object.keys(baseMap).reduce(
      (acc, loanId) => {
        acc[loanId] = {
          ...baseMap[loanId],
          status: localStatusChanges[loanId] || baseMap[loanId].status,
        };
        return acc;
      },
      {} as { [loanId: string]: GetDealDiligence_deal_diligence },
    );
  }, [deal, localStatusChanges]);

  const selectedDiligence = useMemo(
    () => (selectedLoanId ? loanIdToDiligenceMap[selectedLoanId] : null),
    [loanIdToDiligenceMap, selectedLoanId],
  );

  const handleNoteIconClick = useCallback(
    (loanId: string) => {
      setSelectedLoanId(loanId);
      diligenceNoteDialog.setIsOpen(true);
    },
    [diligenceNoteDialog],
  );

  useEffect(() => {
    setLocalStatusChanges({});
    setUpdateTrigger((prev) => prev + 1);
  }, [deal]);

  const handleViewClick = useCallback(
    (id: string) => {
      onViewClick?.(id);
    },
    [onViewClick],
  );

  const handleQuickStatusClick = useCallback(
    (id: string) => {
      onQuickStatusClick?.(id);
    },
    [onQuickStatusClick],
  );

  const handleLoanDiligenceStatusChanged = useCallback(
    (id: string, status: LoanDiligenceStatus) => {
      setLocalStatusChanges((prev) => ({ ...prev, [id]: status }));
      setUpdateTrigger((prev) => prev + 1);
      onLoanDiligenceStatusChanged(id, status);
    },
    [onLoanDiligenceStatusChanged],
  );

  useEffect(() => {
    setLocalStatusChanges({});
  }, [deal]);

  const statusColumn: LoanColumnDef<K> = useMemo(
    () => ({
      header: 'Status',
      id: 'status',
      cell: (props) => {
        const hoverLeftOffset = (props as any).hoverLeftOffset as number;

        const diligence = loanIdToDiligenceMap[props.row.original.id];
        if (!diligence) {
          return <div>No diligence data</div>;
        }

        const status = diligence.status;
        const { hasNotes } = useDiligenceNotes(diligence.id);

        return (
          <div className="flex items-center">
            <div className="hidden group-hover:block">
              <div
                className={`absolute bottom-0 right-0 z-10 flex  items-center justify-end gap-6`}
                style={{ transform: `translateX(-${hoverLeftOffset}px)` }}
              >
                <button
                  className="text-[#d743bd] underline hover:text-accent2-emphasis"
                  type="button"
                  onClick={() => handleViewClick(props.row.original.id)}
                >
                  View
                </button>
                <button
                  className="text-[#d743bd] underline hover:text-accent2-emphasis"
                  type="button"
                  onClick={() => handleQuickStatusClick(props.row.original.id)}
                >
                  Quick Status View
                </button>
                <IconButton
                  onClick={() => handleNoteIconClick(props.row.original.id)}
                  variant="icon-only"
                  icon={
                    <IconNote
                      stroke={2}
                      className={`${hasNotes ? 'text-warning-default' : 'text-white'}`}
                    />
                  }
                />
              </div>
            </div>

            {role === DealRole.SELLER ? (
              <div>{status}</div>
            ) : (
              <LoanStatusDropdown
                value={status}
                onChange={(newStatus) =>
                  handleLoanDiligenceStatusChanged(props.row.original.id, newStatus)
                }
              />
            )}
          </div>
        );
      },
    }),
    [
      loanIdToDiligenceMap,
      role,
      handleLoanDiligenceStatusChanged,
      handleViewClick,
      handleQuickStatusClick,
      handleNoteIconClick,
    ],
  );

  const columns = useMemo(() => {
    const defaultColumns = getDefaultColumns(
      assetClass,
      false,
      state.globalTableOrderConfig.myPortfolio.loanTable[assetClass] ?? [],
    );

    // Always put status column first, remove it from default columns if present
    const filteredColumns = defaultColumns.filter((col) => col.id !== 'status');

    const finalColumns = [statusColumn, ...filteredColumns];

    return finalColumns;
  }, [
    statusColumn,
    assetClass,
    state.globalTableOrderConfig.myPortfolio.loanTable[assetClass],
    computedColumnOrder,
  ]);
  /* eslint-enable */ //TODO: fixed React Hook "useDialog" is called in function

  if (!deal) {
    return null;
  }

  return (
    <div className="relative">
      {loanDiligence.processingLoanIds.length > 0 && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50">
          <div className="text-xl text-white">
            Processing diligence for {loanDiligence.processingLoanIds.length} loans... please wait.
          </div>
        </div>
      )}
      <DataContext.Provider value={{ state: stateWithStatusFirst, dispatch }}>
        <LoanTable
          key={`loan-table-${updateTrigger}`}
          data={diligenceLoans}
          strings={{
            empty:
              role === DealRole.SELLER
                ? 'Waiting for buyer to select loans'
                : 'Please select loans',
          }}
          sorting={sorting}
          assetClass={assetClass}
          // @ts-expect-error - LoanColumnDef type mismatch with generic constraints, needs refactoring
          columns={columns}
          pagination={pagination}
          omitColumnIds={role === DealRole.BUYER ? ['listing'] : []}
          setColumnOrder={setColumnOrder}
        />
      </DataContext.Provider>
      {selectedDiligence && (
        <DiligenceNotesDialog
          dialog={diligenceNoteDialog}
          documentName={selectedDiligence?.loan.account_id || 'Unknown Loan'}
          diligenceId={selectedDiligence?.id}
        />
      )}
    </div>
  );
};

export const ReviewDiligenceTable = memo(_ReviewDiligenceTable, (prevProps, nextProps) => {
  return (
    prevProps.deal === nextProps.deal &&
    prevProps.role === nextProps.role &&
    prevProps.assetClass === nextProps.assetClass &&
    isEqual(prevProps.sorting, nextProps.sorting) &&
    isEqual(prevProps.pagination, nextProps.pagination)
  );
});
